import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, Link2, Unlink } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { ApiError } from "@/api/client";
import { documentsApi } from "@/api/documents";
import { documentRelationshipsApi } from "@/api/documentRelationships";
import { useDocumentRelationships } from "@/hooks/useDocumentRelationships";
import { relationshipDisplayLabel } from "@/api/relationshipLabels";
import { useToast } from "@/components/ui/Toast";
import { useDialogs } from "@/hooks/useDialogs";
import type { DocumentRelationshipDto } from "@/types/documentRelationships";

interface Props {
  documentId: string;
  onOpenDocument: (id: string) => void;
  onLinkAnother: () => void;
  /** Wired by the parent so folder lists (and linkedCount chain icons)
   * refresh after an unlink. */
  onUnlinked?: () => void;
}

export function LinkedDocumentsSection({
  documentId,
  onOpenDocument,
  onLinkAnother,
  onUnlinked,
}: Props) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const dialogs = useDialogs();
  const query = useDocumentRelationships(documentId);

  const unlink = useMutation({
    mutationFn: (id: string) => documentRelationshipsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["document-relationships", documentId] });
      onUnlinked?.();
      toast({ message: "Documents unlinked.", tone: "success" });
    },
    onError: (err) => {
      const message =
        err instanceof ApiError
          ? err.errors[0] ?? err.message
          : err instanceof Error
            ? err.message
            : "Could not unlink the documents.";
      toast({ message, tone: "error" });
    },
  });

  const confirmUnlink = async (rel: DocumentRelationshipDto) => {
    let message = "Unlink these two documents? The relationship can be re-created later.";
    if (rel.createdByUserId === null) {
      message +=
        " Auto-created relationships will be re-created the next time the system runs the relevant pipeline.";
    }
    const ok = await dialogs.confirm({ title: "Unlink documents", message });
    if (ok) unlink.mutate(rel.id);
  };

  const relationships = query.data ?? [];

  return (
    <div className="mt-space-3 pt-space-2 border-t border-border-light">
      <div className="flex items-center gap-space-2 mb-space-2">
        <h3 className="font-display font-semibold text-small text-onyx m-0">
          Linked documents
        </h3>
        {relationships.length > 0 && (
          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-[5px] rounded-full bg-warm-white text-gray-mid text-caption font-display font-semibold">
            {relationships.length}
          </span>
        )}
      </div>

      {query.isLoading ? (
        <div className="flex flex-col gap-space-2">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="h-10 rounded-md bg-warm-white animate-pulse"
              aria-hidden
            />
          ))}
        </div>
      ) : query.isError ? (
        <div className="text-small text-gray-mid">
          Couldn&apos;t load linked documents.
          <Button
            variant="tertiary"
            size="sm"
            className="ml-space-2"
            onClick={() => query.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : relationships.length === 0 ? (
        <div className="text-small text-gray-mid">
          No linked documents yet.
          <Button
            variant="tertiary"
            size="sm"
            className="ml-space-2"
            onClick={onLinkAnother}
          >
            <Link2 size={14} aria-hidden /> Link to another document
          </Button>
        </div>
      ) : (
        <ul className="m-0 p-0 list-none flex flex-col gap-space-1">
          {relationships.map((rel) => (
            <li
              key={rel.id}
              className="flex items-center gap-space-2 py-space-1"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-space-2">
                  <span className="text-caption text-gray-mid uppercase tracking-label">
                    {relationshipDisplayLabel(rel.kind, rel.direction)}
                  </span>
                  {rel.createdByUserId === null && (
                    <span className="px-space-1 rounded-sm bg-warm-white text-gray-mid text-caption">
                      auto
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="text-small font-semibold text-onyx hover:underline text-left truncate max-w-full"
                  onClick={() => onOpenDocument(rel.relatedDocumentId)}
                >
                  {rel.relatedDocument.title}
                </button>
              </div>
              <Button
                variant="tertiary"
                size="sm"
                onClick={async () => {
                  const res = await documentsApi.download(rel.relatedDocumentId);
                  window.open(res.sasUrl, "_blank", "noopener");
                }}
              >
                <Download size={14} aria-hidden /> Open
              </Button>
              <Button
                variant="tertiary"
                size="sm"
                disabled={unlink.isPending}
                onClick={() => confirmUnlink(rel)}
              >
                <Unlink size={14} aria-hidden /> Unlink
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
